import amplitude from 'amplitude-js';

import { Actions } from 'enums/gladlyapi';
import { analyticsEventTypes } from 'events/analytics';
import { appEventTypes } from 'events/app';
import snowplowTypes, { setSnowplowUserId, trackSnowplowEvent } from 'lib/analytics';

// Amplitude Event Types
const LOG_EVENT = 'logEvent';
const IDENTIFY = 'identify';

/*
 * Map action types to analytics event names
 *
 * Choose event names carefully as they cannot be changed once used in production.
 *
 * Each event map item should return the following object:
 * [ { eventType: ..., eventPayload: ... }]
 */
const generateActionEventMap = () => ({
  [appEventTypes.AUTHENTICATION_SUCCESS]: (getState, { payload: { gladlyCustomerId } }) => {
    if (gladlyCustomerId) {
      // Snowplow tracking
      setSnowplowUserId(getState(), gladlyCustomerId);

      // Amplitude tracking
      let identity = new amplitude.Identify().set('customerId', gladlyCustomerId);
      return [
        {
          eventType: IDENTIFY,
          eventPayload: [identity],
        },
      ];
    }
  },
  [appEventTypes.APP_INITIALIZED]: getState => {
    let config = getState().configuration.config;
    let bucketId = getState().configuration.onboardingTypeTestBucket;

    // Snowplow tracking
    trackSnowplowEvent(getState(), snowplowTypes.EVENT_WIDGET_LOADED, {}, []);

    // Amplitude tracking
    let identity = new amplitude.Identify()
      .set('appId', config.appId)
      .set('orgId', config.orgId)
      .set('bucketId', bucketId);

    return [
      {
        eventType: IDENTIFY,
        eventPayload: [identity],
      },
    ];
  },
  [analyticsEventTypes.QUICK_ACTION_SELECTED]: (getState, { payload: { entityId, entityType, label, position } }) => {
    // Snowplow tracking
    trackSnowplowEvent(
      getState(),
      snowplowTypes.EVENT_LINK_CLICKED,
      {
        linkId: entityId,
        linkType: 'quickAction',
        label,
      },
      [
        {
          schema: snowplowTypes.CONTEXT_QUICK_ACTION,
          data: {
            entityId,
            entityType,
            label,
            position,
          },
        },
      ]
    );
  },
  [analyticsEventTypes.SCREEN_SEARCH_RESULTS_VIEWED]: (
    getState,
    { payload: { searchId, searchResults, searchTerm } }
  ) => {
    // Snowplow tracking
    trackSnowplowEvent(getState(), snowplowTypes.EVENT_SCREEN_VIEWED, {}, [
      {
        schema: snowplowTypes.CONTEXT_SEARCH,
        data: {
          searchTerm,
          id: searchId,
          countSearchResults: searchResults.length,
        },
      },
      ...searchResults.map(result => ({
        schema: snowplowTypes.CONTEXT_SEARCH_RESULT,
        data: result,
      })),
    ]);

    // Amplitude tracking
    return [
      {
        eventType: LOG_EVENT,
        eventPayload: ['Searched', { searchTerm, resultIds: searchResults.map(sr => sr.entityId) }],
      },
    ];
  },
  [analyticsEventTypes.SEARCHED]: (getState, { payload: { searchId, searchTerm } }) => {
    // Snowplow tracking
    trackSnowplowEvent(getState(), snowplowTypes.EVENT_SEARCHED, {}, [
      {
        schema: snowplowTypes.CONTEXT_SEARCH,
        data: {
          id: searchId,
          searchTerm,
          countSearchResults: -1,
        },
      },
    ]);
  },
  [analyticsEventTypes.SEARCH_RESULT_SELECTED]: (getState, { payload: { entityId, entityType, label, position } }) => {
    // Snowplow tracking
    let searchState = getState().selfService.search;
    let labelText = htmlToText(label);
    trackSnowplowEvent(
      getState(),
      snowplowTypes.EVENT_LINK_CLICKED,
      { linkId: entityId, linkType: 'searchResult', label: labelText },
      [
        {
          schema: snowplowTypes.CONTEXT_SEARCH,
          data: {
            searchTerm: searchState.searchTerm,
            id: searchState.timestamp,
            countSearchResults: searchState.results.length,
          },
        },
        {
          schema: snowplowTypes.CONTEXT_SEARCH_RESULT,
          data: { entityId, entityType, position },
        },
      ]
    );

    // Amplitude tracking
    return [
      {
        eventType: LOG_EVENT,
        eventPayload: ['Search Result Selected', entityId],
      },
    ];
  },
  [Actions.GLADLY_API_SHOW]: (getState, { payload: { isVisible } }) => {
    // Amplitude tracking - can be deprecated with Snowplow's `EVENT_WIDGET_OPENED` tracking (with `trigger = 'api'`)
    return [
      {
        eventType: LOG_EVENT,
        eventPayload: ['API show', { isVisible }],
      },
    ];
  },
  [analyticsEventTypes.WIDGET_OPENED]: (getState, { payload: { trigger } }) => {
    // Snowplow tracking
    trackSnowplowEvent(getState(), snowplowTypes.EVENT_WIDGET_OPENED, { trigger });

    // Amplitude tracking
    const user = getState().user;

    if (!user || !user.id) {
      // A user has an id once a user goes through
      // onboarding or send their first message
      return [
        {
          eventType: LOG_EVENT,
          eventPayload: ['Widget Opened'],
        },
      ];
    }
  },
  [analyticsEventTypes.ONBOARDING_SUBMITTED]: getState => {
    // Snowplow tracking
    trackSnowplowEvent(getState(), snowplowTypes.EVENT_ONBOARDING_SUBMITTED);

    // Amplitude tracking
    return [
      {
        eventType: LOG_EVENT,
        eventPayload: ['Onboarding Form Submitted'],
      },
    ];
  },
  [analyticsEventTypes.CONTACT_US_CLICKED]: (getState, { payload: { label } }) => {
    // Snowplow tracking
    trackSnowplowEvent(getState(), snowplowTypes.EVENT_LINK_CLICKED, {
      linkId: 'chat with an agent button',
      linkType: 'internalLink',
      label,
    });
  },
  [analyticsEventTypes.CHAT_MESSAGE_SENT]: (getState, { payload: { messageId, messageLength } }) => {
    // Snowplow tracking
    trackSnowplowEvent(getState(), snowplowTypes.EVENT_CHAT_MESSAGE_SENT, { messageId, messageLength });

    // Amplitude tracking
    return [
      {
        eventType: LOG_EVENT,
        eventPayload: [
          'Message Sent',
          {
            messageId,
          },
        ],
      },
    ];
  },
  [analyticsEventTypes.CHAT_MESSAGE_RECEIVED]: (getState, { payload: { messageId, messageLength } }) => {
    // Snowplow tracking
    trackSnowplowEvent(getState(), snowplowTypes.EVENT_CHAT_MESSAGE_RECEIVED, { messageId, messageLength });

    // Amplitude tracking
    return [
      {
        eventType: LOG_EVENT,
        eventPayload: [
          'Message Received',
          {
            messageId,
          },
        ],
      },
    ];
  },
});

export default context => {
  amplitude.getInstance().init(context.config.amplitudeApiKey);

  const actionEventMapper = generateActionEventMap();

  return store => next => action => handleAction(store.getState.bind(store), next, action, actionEventMapper);
};

function handleAction(getState, next, action, actionEventMapper) {
  if (action && actionEventMapper[action.type]) {
    const analytics = actionEventMapper[action.type](getState, action);
    if (analytics) {
      analytics.forEach(a => handleAnalytic(a));
    }
  }
  return next(action);
}

function handleAnalytic(analytic) {
  const type = analytic.eventType;

  // In case the eventType was not specified or set to `null`, ignore this individual spec.
  if (type && type.length) {
    const fields = analytic.eventPayload || {};

    if (fields instanceof Error) return console.warn(fields); // eslint-disable-line no-console

    emit(type, fields);
  }
}

function emit(type, fields) {
  const client = amplitude.getInstance();
  if (client && typeof client[type] === 'function') {
    client[type](...fields);
  }
}

function htmlToText(html) {
  var temp = document.createElement('div');
  temp.innerHTML = html;
  return temp.textContent || temp.innerText || '';
}
